<template>
	<div class="icon-select">
		<button
			ref="triggerRef"
			class="icon-select__trigger"
			type="button"
			:class="{ 'icon-select__trigger--disabled': disabled }"
			:disabled="disabled"
			@click="toggle">
			<FontAwesomeIcon
				v-if="modelValue"
				class="icon-select__current-icon"
				:icon="modelValue" />
			<span
				v-else
				class="icon-select__placeholder"
				>Select icon</span
			>
		</button>
		<Popover
			ref="popoverRef"
			class="icon-select__popover"
			@show="onShow">
			<div class="icon-select__content">
				<InputText
					ref="searchInputRef"
					v-model="searchTerm"
					class="icon-select__search"
					placeholder="Search icons..."
					@input="debouncedSearch" />
				<div
					v-if="loading"
					class="icon-select__loading">
					<i class="fal fa-spinner-third fa-spin" />
				</div>
				<div
					v-else-if="searchFailed"
					class="icon-select__message"
					role="alert">
					Couldn't load icons. Try searching again.
				</div>
				<div
					v-else-if="!icons.length"
					class="icon-select__message"
					role="status">
					No icons found.
				</div>
				<div
					v-else
					class="icon-select__grid">
					<button
						v-for="icon in icons"
						:key="icon.icon"
						class="icon-select__icon-button"
						type="button"
						:class="{ 'icon-select__icon-button--selected': icon.icon === modelValue }"
						:title="icon.label"
						@click="selectIcon(icon.icon)">
						<FontAwesomeIcon :icon="icon.icon" />
					</button>
				</div>
			</div>
		</Popover>
	</div>
</template>

<script setup lang="ts">
import FontAwesomeApi, { type Icon } from '@/helpers/integrations/fontAwesomeApi'
import Popover from 'primevue/popover'
import InputText from 'primevue/inputtext'
import FontAwesomeIcon from '@/components/FontAwesomeIcon.vue'
import { captureException } from '@sentry/vue'
import { ref, nextTick, onBeforeUnmount, type ComponentPublicInstance } from 'vue'

const emit = defineEmits<{
	(e: 'update:modelValue', value: string): void
}>()

const props = defineProps<{
	modelValue: string
	disabled?: boolean
}>()

const fontAwesomeApi = new FontAwesomeApi()

const popoverRef = ref<InstanceType<typeof Popover> | null>(null)
const triggerRef = ref<HTMLButtonElement | null>(null)
const searchInputRef = ref<ComponentPublicInstance<typeof InputText> | null>(null)

const searchTerm = ref('')
const icons = ref<(Icon & { icon: string; prefix: string })[]>([])
const loading = ref(false)
const searchFailed = ref(false)

let debounceTimeout: ReturnType<typeof setTimeout> | null = null
let searchId = 0

onBeforeUnmount(() => {
	if (debounceTimeout) clearTimeout(debounceTimeout)
	searchId++
	fontAwesomeApi.requestController.abort()
})

function toggle(event: Event) {
	popoverRef.value?.toggle(event)
}

function onShow() {
	// Populate search with current icon name if set
	if (props.modelValue) {
		const iconName = props.modelValue.split(' ')[1]?.replace('fa-', '') ?? ''
		searchTerm.value = iconName
	}
	// Load icons and focus search input
	search(searchTerm.value)
	nextTick(() => {
		searchInputRef.value?.$el?.focus()
		searchInputRef.value?.$el?.select()
	})
}

function debouncedSearch() {
	if (debounceTimeout) {
		clearTimeout(debounceTimeout)
	}
	debounceTimeout = setTimeout(() => {
		search(searchTerm.value)
	}, 300)
}

async function search(searchString: string) {
	const currentSearchId = ++searchId
	loading.value = true
	searchFailed.value = false
	try {
		const res = await fontAwesomeApi.search(searchString)
		if (!res || currentSearchId !== searchId) return

		icons.value = (res.data.data?.search ?? []).flatMap((icon) => {
			const styles = icon.familyStylesByLicense?.pro ?? []
			const prefix = (styles.find((style) => style.prefix === 'far') ?? styles[0])?.prefix
			if (!prefix) return []

			return [{ ...icon, id: `fa-${icon.id}`, icon: `${prefix} fa-${icon.id}`, prefix }]
		})
	} catch (error: unknown) {
		if (currentSearchId !== searchId) return
		icons.value = []
		searchFailed.value = true
		captureException(error)
	} finally {
		if (currentSearchId === searchId) loading.value = false
	}
}

function selectIcon(icon: string) {
	emit('update:modelValue', icon)
	popoverRef.value?.hide()
}
</script>

<style lang="scss">
.icon-select {
	display: inline-flex;

	.icon-select__trigger {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 60px;
		height: 60px;
		padding: 0;
		background: transparent;
		border-radius: var(--p-inputtext-border-radius);
		cursor: pointer;
		transition: border 0.2s;
		border: solid 1px #ffffff00;

		&:hover {
			border: solid 1px var(--p-inputtext-hover-border-color);
		}

		&:focus {
			outline: none;
			border-color: var(--p-inputtext-focus-border-color);
		}

		&--disabled {
			opacity: 0.6;
			cursor: default;
			pointer-events: none;
		}
	}

	.icon-select__current-icon {
		font-size: 32px;
		color: var(--p-inputtext-color);
	}

	.icon-select__placeholder {
		font-size: 32px;
		color: var(--p-inputtext-placeholder-color);
	}
}

.p-popover.icon-select__popover {
	.icon-select__content {
		display: flex;
		flex-direction: column;
		gap: 12px;
		width: 100vw;
		max-width: 320px;
		max-height: 400px;
	}

	.icon-select__search {
		width: 100%;
	}

	.icon-select__loading {
		display: flex;
		align-items: center;
		justify-content: center;
		height: 200px;
		font-size: 24px;
		color: var(--p-text-muted-color);
	}

	.icon-select__message {
		padding: 12px;
		color: var(--p-text-muted-color);
		text-align: center;
	}

	.icon-select__grid {
		display: grid;
		grid-template-columns: repeat(6, 1fr);
		gap: 4px;
		max-height: 320px;
		overflow-y: auto;
		padding: 4px;
	}

	.icon-select__icon-button {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 46px;
		height: 46px;
		padding: 0;
		font-size: 24px;
		color: var(--p-primary-color);
		background: transparent;
		border: none;
		border-radius: var(--p-border-radius-sm);
		cursor: pointer;
		transition:
			background-color 0.15s,
			color 0.15s;

		&:hover {
			background: var(--p-surface-700);
		}

		&--selected {
			background: var(--p-primary-color);
			color: var(--p-primary-contrast-color);

			&:hover {
				background: var(--p-primary-hover-color);
			}
		}
	}
}
</style>
